import { TIME_ZONES } from "../../constants";
import { formatTimeZoneOffset } from "../../utils/timeZoneOffset";

interface AppTimeZone {
	ianaCode: string;
	name: string;
	cities?: readonly string[];
}

const APP_TIME_ZONES = TIME_ZONES as readonly AppTimeZone[];

export interface TimeZoneSelectItem {
	ianaCode: string;
	name: string;
	cities: readonly string[];
	currentOffset: string;
}

export interface TimeZoneChoice {
	value: string;
	label: string;
	placeholder: boolean;
	customProperties: {
		searchBy: string[];
		selectedDisplay: string;
	};
}

export const TIME_ZONE_PLACEHOLDER = "Select a timezone";
export const TIME_ZONE_FUSE_OPTIONS = Object.freeze({
	caseSensitive: false,
	include: "score",
	minMatchCharLength: 1,
	shouldSort: true,
	threshold: 0.35
});

const EMPTY_TIME_ZONE: TimeZoneSelectItem = Object.freeze({
	ianaCode: "",
	name: "",
	cities: [],
	currentOffset: ""
});

export function buildTimeZoneSelectItems(
	date = new Date()
): TimeZoneSelectItem[] {
	const timeZones = APP_TIME_ZONES.map(timeZone => ({
		ianaCode: timeZone.ianaCode,
		name: timeZone.name,
		cities: [...(timeZone.cities ?? [])],
		currentOffset: formatTimeZoneOffset(timeZone.ianaCode, date)
	}));

	return [EMPTY_TIME_ZONE, ...timeZones];
}

export function buildTimeZoneChoice(timeZone: TimeZoneSelectItem): TimeZoneChoice {
	const hasTimeZone = timeZone.ianaCode !== "";
	const cityDisplay = timeZone.cities.join(", ");
	const details = [timeZone.currentOffset, cityDisplay].filter(Boolean).join(" · ");

	return {
		value: timeZone.ianaCode,
		label: hasTimeZone
			? `<span class="timezone-select-option"><span class="timezone-select-option-label">${timeZone.name}</span><small class="timezone-select-option-details">${details}</small></span>`
			: TIME_ZONE_PLACEHOLDER,
		placeholder: !hasTimeZone,
		customProperties: {
			searchBy: [
				timeZone.name,
				timeZone.ianaCode,
				timeZone.currentOffset,
				...timeZone.cities
			].filter(Boolean),
			selectedDisplay: hasTimeZone
				? `<span class="timezone-select-selected-option"><span>${timeZone.name}</span><small>${timeZone.currentOffset}</small></span>`
				: TIME_ZONE_PLACEHOLDER
		}
	};
}

export function getSelectedTimeZoneDisplay(
	value: string | undefined,
	choices: readonly TimeZoneChoice[]
): string {
	return choices.find(choice => choice.value === value)
		?.customProperties.selectedDisplay ?? TIME_ZONE_PLACEHOLDER;
}
